import React from "react";
import { C, fmt, fyl } from "./shared";

const inputCls = "w-full rounded px-2.5 py-2 text-sm font-semibold focus:outline-none";
const inputStyle = { backgroundColor: "rgba(96,165,250,0.12)", border: "1px solid rgba(96,165,250,0.35)", color: "#93C5FD" };

const DELTA_FIELDS = [
  ["volume_delta", "Volume", "−20 = 20% fewer"],
  ["price_delta", "Price", "−10 = 10% cheaper"],
  ["cogs_delta", "COGS", "+10 = 10% costlier"],
  ["opex_delta", "Opex", "+10 = 10% higher"],
];

export default function Scenarios({ out, model, save }) {
  const sc = out.scenarios;
  const yl = fyl(out);
  const deltas = sc.deltas;
  const updDelta = (name, k, v) =>
    save({ assumptions: { scenarios: { ...(model.assumptions.scenarios || {}), [name]: { ...deltas[name], [k]: v } } } });

  const rows = [
    [`${yl[0]} revenue`, (s) => fmt(s.annual.f1.revenue)],
    [`${yl[4]} revenue`, (s) => fmt(s.annual.f5.revenue)],
    [`${yl[0]} EBITDA`, (s) => fmt(s.annual.f1.ebitda)],
    [`${yl[4]} EBITDA`, (s) => fmt(s.annual.f5.ebitda)],
    [`${yl[4]} net profit`, (s) => fmt(s.annual.f5.net_profit)],
    ["Lowest cash point", (s) => `${fmt(s.min_cash)} (${s.min_cash_label || "M" + s.min_cash_month})`],
    ["First cash shortfall", (s) => (s.first_shortfall_month ? s.first_shortfall_label : "None")],
    [`${yl[4]} closing cash`, (s) => fmt(s.f5_closing_cash)],
  ];
  const cols = [["base", "Base", C.tealSoft], ["downside", "Downside", C.red], ["upside", "Upside", C.green]];

  return (
    <div data-testid="fm-scenarios" className="max-w-4xl space-y-5">
      <div className="text-xs text-white/45">
        Section I · Scenario analysis — the base case is your current assumptions. Downside and upside stress the key drivers so you can answer "what if it goes slower?" before a funder asks it.
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {["downside", "upside"].map((name) => (
          <div key={name} className="rounded-xl p-4" style={{ backgroundColor: C.panel, border: `1px solid ${name === "downside" ? "rgba(248,113,113,0.25)" : "rgba(44,197,168,0.25)"}` }}>
            <div className="text-sm font-extrabold mb-3" style={{ color: name === "downside" ? C.red : C.green }}>
              {name === "downside" ? "Downside deltas" : "Upside deltas"}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {DELTA_FIELDS.map(([k, label, hint]) => (
                <div key={k}>
                  <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/50 mb-1">{label}<div className="text-white/30 normal-case tracking-normal">{hint}</div></div>
                  <div className="relative">
                    <input type="number" step="5" className={inputCls + " pr-7"} style={inputStyle}
                      data-testid={`fm-scenario-${name}-${k}`}
                      value={deltas[name]?.[k] == null ? "" : Math.round(deltas[name][k] * 10000) / 100} placeholder="0"
                      onChange={(e) => updDelta(name, k, e.target.value === "" ? 0 : parseFloat(e.target.value) / 100)} />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs font-bold pointer-events-none" style={{ color: "rgba(147,197,253,0.6)" }}>%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="rounded-lg overflow-x-auto" style={{ border: "1px solid rgba(123,211,206,0.12)" }}>
        <table className="text-xs w-full" data-testid="fm-scenario-table">
          <thead><tr style={{ backgroundColor: C.panel }}>
            <th className="px-3 py-2 text-left text-white/50 font-bold">Outcome</th>
            {cols.map(([id, label, color]) => <th key={id} className="px-3 py-2 text-right font-bold" style={{ color }}>{label}</th>)}
          </tr></thead>
          <tbody>
            {rows.map(([label, f]) => (
              <tr key={label} className="border-t border-white/5">
                <td className="px-3 py-1.5 text-white/60 whitespace-nowrap">{label}</td>
                {cols.map(([id]) => <td key={id} className="px-3 py-1.5 text-right text-white/80 whitespace-nowrap">{f(sc[id])}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
